import React, { type ComponentProps, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import {
  BodyText,
  DisplayText,
  Scrollspy,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

import { messages as globalMessages } from '../../CustomizationIngredientsGrid.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridSection = (
  props: CustomizationIngredientsGridSectionProps,
) => {
  const {
    sectionId,
    ingredientKindName,
    subheading,
    helperText,
    children,
    withBorder,
    dataSet,
    ...restProps
  } = props;

  const { match } = useResponsive();
  const containerSpacing = useContainerSpacing();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useMemo<ViewStyle>(() => {
    return { paddingHorizontal: containerSpacing };
  }, [containerSpacing]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Scrollspy.TargetView
      style={[
        match([styles.containerXS, styles.containerSM]),
        containerDynamicStyles,
      ]}
      targetId={sectionId}
      {...(dataSet ? { dataSet } : {})}
      {...restProps}
    >
      <View style={styles.label}>
        <DisplayText sizeMatch={['24']} bold>
          <FormattedMessage
            {...globalMessages.ingredientKind}
            values={{ ingredient_kind: ingredientKindName }}
          />
        </DisplayText>

        {subheading ? (
          <BodyText
            style={match([styles.subheadingXS, styles.subheadingSM])}
            sizeMatch={['16', '18']}
            bold={match([false, true])}
          >
            {subheading}
          </BodyText>
        ) : null}
      </View>

      {helperText ? (
        <BodyText style={styles.helperLabel}>{helperText}</BodyText>
      ) : null}

      <View
        style={[
          styles.contentContainer,
          withBorder ? styles.contentContainerWithBorder : undefined,
        ]}
      >
        {children}
      </View>
    </Scrollspy.TargetView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingTop: theme.spacing['8'],
  },
  containerSM: {
    paddingTop: theme.spacing['10'],
  },
  contentContainer: {
    marginTop: theme.spacing['4'],
  },
  contentContainerWithBorder: {
    paddingBottom: theme.spacing['6'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subheadingXS: {
    color: theme.colors.CHARCOAL,
  },
  subheadingSM: {
    color: theme.colors.BLACK,
  },
  helperLabel: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationIngredientsGridSectionProps = {
  sectionId: string;
  ingredientKindName: string;
  subheading?: string;
  helperText?: string;
  withBorder?: boolean;
  dataSet?: Record<string, string | number>;
} & Omit<ComponentProps<typeof Scrollspy.TargetView>, 'targetId'>;
